import { createStore } from 'vuex';
import { RequestSession } from './interfaces/requestSession';
import { Donation } from '@/interfaces/donation/donation';
import { Invoice } from '@/interfaces/economy/invoice';
import { Snackbar } from '@/interfaces/presentation/snackbar';
import { Dialog } from '@/interfaces/presentation/dialog';
import { DonationGuideVersion } from '@/enums/donationGuideVersion';
import LocationService from '@/services/locationService';

class State {
  requestSession: RequestSession | null = null;
  donation: Donation | null = null;
  userLocation: string | null = null;
  snackbar: Snackbar | null = null;
  invoice: Invoice | null = null;
  currentStep: number = 0;
  hideTaxReductionInfo: boolean = false;
  donationGuideVersion: DonationGuideVersion = DonationGuideVersion.V2; // Default version, can be changed based on session
  dialog: Dialog = {
    active: false,
    title: '',
    text: '',
    closeBtnText: '',
    actionBtnText: '',
    actionClick: () => {},
  };
}

export default createStore({
  state: new State(),
  mutations: {
    updateRequestSession(state, session: RequestSession) {
      state.requestSession = session;
    },
    setLocation(state, countryCode: string | null) {
      state.userLocation = countryCode;
    },
    updateDonation(state, payload: Donation) {
      state.donation = payload;
    },
    updateCurrentStep(state, payload: number) {
      state.currentStep = payload;
    },
    createdInvoice(state, payload: Invoice) {
      state.invoice = payload;
    },
    setDonationGuideVersion(state, version: DonationGuideVersion) {
      state.donationGuideVersion = version;
    },
    setSnackbar(state: State, snackbar: Snackbar) {
      // Default values
      const active = typeof snackbar.active !== 'undefined' ? snackbar.active : true;
      const color = snackbar.color ? snackbar.color : 'primary';
      const timeout = snackbar.timeout ? snackbar.timeout : 6000; // 6 sec
      const text = snackbar.text ? snackbar.text : 'Viktigt meddelande';

      // hide snackbar incase it is already showing
      if (state.snackbar?.active) {
        state.snackbar.active = false;
      }
      // then we set the snackbar with proper values after a small delay
      window.setTimeout(() => {
        state.snackbar = {
          active: active,
          color: color,
          timeout: timeout,
          text: text,
          action: snackbar.action,
        };
      }, 100);
    },
    setDialog(state, dialog) {
      // dialog is used instead of confirmation alerts (e.g when confirming to delete an item)
      // Default values
      const active = typeof dialog.active !== 'undefined' ? dialog.active : true;
      if (state.dialog?.active) {
        dialog.active = active;
      }
      // hide dialog incase it is already showing (should not happen)
      state.dialog.active = false;

      // then we set the dialog with proper values after a small delay
      window.setTimeout(() => {
        state.dialog = dialog;
      }, 100);
    },
    updateHideTaxReductionInfo(state, value: boolean) {
      state.hideTaxReductionInfo = value;
    },
  },
  actions: {
    async fetchUserLocation({ commit }) {
      try {
        const locationService = new LocationService();
        const country = await locationService.getUserCountry();
        commit('setLocation', country);
      } catch {
        commit('setSnackbar', {
          active: true,
          color: 'error',
          text: 'Failed to fetch user location',
        });
        commit('setLocation', null);
      }
    },
  },
});
